import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { API_BASE_URL } from '../../config';

const BlogManagement = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchBlogs();
    fetchStats();
  }, [statusFilter]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await axios.get(`${API_BASE_URL}/api/admin/blogs`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params
      });

      setBlogs(response.data.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/blogs/statistics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStats(response.data.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this market intelligence article?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/api/admin/blogs/${blogId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchBlogs();
      fetchStats();
    } catch (err) {
      alert(`Error deleting blog: ${err.message}`);
    }
  };

  const handlePublish = async (blogId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/admin/blogs/${blogId}/publish`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchBlogs();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.error || `Error publishing blog: ${err.message}`);
    }
  };

  const handleSubmitReview = async (blogId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/admin/blogs/${blogId}/submit-review`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchBlogs();
      alert('Article submitted for human review.');
    } catch (err) {
      alert(err.response?.data?.error || `Could not submit for review: ${err.message}`);
    }
  };

  const handleArchive = async (blogId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/admin/blogs/${blogId}/archive`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchBlogs();
      fetchStats();
    } catch (err) {
      alert(`Error archiving blog: ${err.message}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEditorialStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'human_approved': return 'bg-emerald-100 text-emerald-800';
      case 'awaiting_human_review': return 'bg-blue-100 text-blue-800';
      case 'changes_requested': return 'bg-amber-100 text-amber-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredBlogs = blogs.filter(blog =>
    searchQuery === '' ||
    blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    blog.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Editorial Office</h1>
            <p className="text-gray-600 mt-1">Draft, evidence, review and publish market intelligence</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              ← Back to Dashboard
            </button>
            <button
              onClick={() => navigate('/admin/market-intelligence/new')}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium"
            >
              + New Intelligence Article
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Total Posts</div>
            <div className="text-3xl font-bold text-gray-900">{stats.total_posts}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Published</div>
            <div className="text-3xl font-bold text-green-600">{stats.published_posts}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Drafts</div>
            <div className="text-3xl font-bold text-yellow-600">{stats.draft_posts}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600">Categories</div>
            <div className="text-3xl font-bold text-blue-600">{stats.total_categories}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex items-center space-x-4">
          <input
            type="text"
            placeholder="Search blogs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
          <button
            onClick={() => navigate('/admin/categories')}
            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
          >
            🏷️ Manage Categories
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Blog List */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p>Loading blogs...</p>
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No market intelligence articles found.</p>
            <button
              onClick={() => navigate('/admin/market-intelligence/new')}
              className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            >
              Create Your First Intelligence Article
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Editorial review
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Views
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBlogs.map((blog) => (
                  <tr key={blog.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{blog.title}</div>
                      <div className="text-xs text-gray-500 mt-1">{blog.read_time_minutes} min read</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{blog.category}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(blog.status)}`}>
                        {blog.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getEditorialStatusColor(blog.editorial_status)}`}>
                        {(blog.editorial_status || 'draft').replaceAll('_', ' ')}
                      </span>
                      <div className="mt-1 text-xs text-gray-500">
                        v{blog.publication_version || 1} · {(blog.sources || []).length} source{(blog.sources || []).length === 1 ? '' : 's'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {blog.view_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(blog.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => navigate(`/admin/market-intelligence/${blog.id}/edit`)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </button>
                        {blog.status === 'draft' && ['draft', 'changes_requested', 'rejected'].includes(blog.editorial_status || 'draft') && (
                          <button
                            onClick={() => handleSubmitReview(blog.id)}
                            className="text-orange-600 hover:text-orange-900"
                          >
                            Submit review
                          </button>
                        )}
                        {blog.status === 'draft' && blog.editorial_status === 'awaiting_human_review' && (
                          <button
                            onClick={() => navigate(`/admin/market-intelligence/${blog.id}/edit`)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Review
                          </button>
                        )}
                        {blog.status === 'draft' && blog.editorial_status === 'human_approved' && (
                          <button
                            onClick={() => handlePublish(blog.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Publish approved
                          </button>
                        )}
                        {blog.status === 'published' && (
                          <button
                            onClick={() => handleArchive(blog.id)}
                            className="text-yellow-600 hover:text-yellow-900"
                          >
                            Archive
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(blog.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogManagement;

